import { Button, ButtonVariant } from "@src/components/button/Button";
import Checkbox from "@src/components/vGrid/Checkbox";
import Select, { SingleValue } from "react-select";
import { OptionType, filtersConfig } from "@src/components/utils/reactSelectUtils";
import { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";
import { EventRecord } from "@src/store/pricing/queryEventsSlice";
import React, { useEffect, useMemo, useState } from "react";
import styles from "./InternalInvoiceForm.module.scss";
import { salesActions } from "@src/store";
import { formatCurrency } from "@src/components/utils/numberUtils";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import EventContextHeader from "@src/components/eventContextHeader/EventContextHeader";
import { generateInvoicePdfUrl } from "@src/utils/invoicePrint";
import { toast } from "sonner";

export interface IInternalInvoiceFormProps {
	item?: EventInventoryRecord;
	event?: EventRecord | null;
	seatItems: Array<EventInventoryRecord>;
	onClose?: () => void;
	onSuccess?: () => void;
}

type TPageState = {
	customerId: number | null;
	buyerPoId: string;
	salePrice: string;
	pricePerTicket: string;
	shippingNotes: string;
	selectedSeats: Array<number>;
	markComplete: boolean;
};

const initialPageState: TPageState = {
	customerId: null,
	buyerPoId: "",
	salePrice: "",
	pricePerTicket: "",
	shippingNotes: "",
	selectedSeats: [],
	markComplete: false,
};

const round2 = (n: number): number => Math.round(n * 100) / 100;
const toMoneyString = (n: number): string => (Number.isFinite(n) ? String(round2(n)) : "");

export const InternalInvoiceForm: React.FC<IInternalInvoiceFormProps> = ({ item, seatItems, onClose, onSuccess }) => {
	const dispatch = useAppDispatch();

	const customers = useAppSelector((state) => state.sales.customers);
	const companyName = useAppSelector((state) => state.settings.companyName);
	const theme = useAppSelector((state) => state.settings.theme);
	const loading = useAppSelector((state) => state.sales.createInvoice.loading);

	const [pageState, setPageState] = useState<TPageState>(initialPageState);
	const [pdfUrl, setPdfUrl] = useState<string | null>(null);
	const [printing, setPrinting] = useState(false);

	useEffect(() => {
		if (!customers.init) dispatch(salesActions.listCustomers({}));
	}, []);

	const customerOptions: Array<OptionType> = useMemo(
		() => customers.data.map((c) => ({ value: c.Cust_ID, label: c.Name })),
		[customers.version],
	);
	const selectedCustomer = customers.data.find((c) => c.Cust_ID === pageState.customerId);
	const selectedCustomerOption = customerOptions.find((o) => o.value === pageState.customerId) ?? null;

	const selectedSeatItems = seatItems.filter((s) => pageState.selectedSeats.includes(s.Item_ID));
	const quantity = selectedSeatItems.length;

	const parsedSale = parseFloat(pageState.salePrice);
	const parsedPerTicket = parseFloat(pageState.pricePerTicket);
	const hasSale = pageState.salePrice.trim() !== "" && !isNaN(parsedSale) && parsedSale >= 0;
	const hasPerTicket = pageState.pricePerTicket.trim() !== "" && !isNaN(parsedPerTicket) && parsedPerTicket >= 0;
	const effectivePerTicket = hasPerTicket ? parsedPerTicket : quantity > 0 && hasSale ? parsedSale / quantity : 0;

	const allSeatsSelected = seatItems.length > 0 && pageState.selectedSeats.length === seatItems.length;

	const canSubmit = !!item && quantity > 0 && !!selectedCustomer && (hasSale || hasPerTicket) && !loading;

	const perTicketFromSale = (salePrice: string, qty: number): string => {
		const sale = parseFloat(salePrice);
		return qty > 0 && !isNaN(sale) ? toMoneyString(sale / qty) : "";
	};

	const toggleSeat = (itemId: number) => {
		setPageState((prev) => {
			const selectedSeats = prev.selectedSeats.includes(itemId)
				? prev.selectedSeats.filter((s) => s !== itemId)
				: [...prev.selectedSeats, itemId];
			return { ...prev, selectedSeats, pricePerTicket: perTicketFromSale(prev.salePrice, selectedSeats.length) };
		});
	};

	const toggleSelectAll = () => {
		setPageState((prev) => {
			const selectedSeats = allSeatsSelected ? [] : seatItems.map((s) => s.Item_ID);
			return { ...prev, selectedSeats, pricePerTicket: perTicketFromSale(prev.salePrice, selectedSeats.length) };
		});
	};

	const handleSalePriceChange = (value: string) => {
		setPageState((prev) => ({
			...prev,
			salePrice: value,
			pricePerTicket: perTicketFromSale(value, prev.selectedSeats.length),
		}));
	};

	const handlePerTicketChange = (value: string) => {
		setPageState((prev) => {
			const ppt = parseFloat(value);
			const salePrice = prev.selectedSeats.length > 0 && !isNaN(ppt) ? toMoneyString(ppt * prev.selectedSeats.length) : "";
			return { ...prev, pricePerTicket: value, salePrice };
		});
	};

	const closeAndReset = () => {
		setPageState(initialPageState);
		onClose?.();
	};

	const handleSubmit = async (print: boolean) => {
		if (!canSubmit || !item || !selectedCustomer) return;

		try {
			const res = await dispatch(
				salesActions.createInvoice({
					pricePerTicket: effectivePerTicket,
					CustCompanyName: selectedCustomer.Name,
					CompanyName: companyName,
					CustomerId: selectedCustomer.Cust_ID,
					Complete: pageState.markComplete,
					ShippingNotes: pageState.shippingNotes,
					BuyerPoId: pageState.buyerPoId,
					Items: selectedSeatItems,
					IsExchange: selectedCustomer.Is_Exchange,
				}),
			).unwrap();
			toast.success("Invoice created.");
			onSuccess?.();

			if (print && res?.invoiceId) {
				setPrinting(true);
				const url = await generateInvoicePdfUrl(res.invoiceId);
				setPrinting(false);
				if (url) {
					setPdfUrl(url);
					return;
				}
				toast.warning("Invoice created, but the PDF preview could not be generated.");
			}

			closeAndReset();
		} catch {
			setPrinting(false);
			toast.error("Failed to create invoice.");
		}
	};

	const seatLabel = (seat: EventInventoryRecord): string => `Seat ${seat.Seat_Number}`;
	const availableSeats: Array<OptionType<number>> = seatItems.map((s) => ({ value: s.Item_ID, label: seatLabel(s) }));

	if (pdfUrl) {
		return (
			<div className={styles.pdfRoot}>
				<object data={pdfUrl} type="application/pdf" className={styles.pdfViewer} aria-label="Invoice PDF">
					<iframe title="Invoice PDF" src={pdfUrl} className={styles.pdfViewer} />
				</object>
			</div>
		);
	}

	return (
		<div className={styles.root}>
			<EventContextHeader padding listings={item ? [{ section: item.Section, row: item.Row, seats: item.Seats }] : []} />
			<div className={styles.body}>
				<div className={styles.mainCol}>
					<div className={styles.colTitle}>Internal Invoice</div>

					<div className={styles.twoCol}>
						<div className={styles.field}>
							<div className={styles.fieldLabel}>
								Customer <span className={styles.required}>*</span>
							</div>
							<Select<OptionType, false>
								options={customerOptions}
								value={selectedCustomerOption}
								isLoading={customers.loading}
								isSearchable
								placeholder="Choose customer"
								styles={filtersConfig("", theme)}
								onChange={(opt: SingleValue<OptionType>) =>
									setPageState((prev) => ({ ...prev, customerId: opt ? Number(opt.value) : null }))
								}
							/>
						</div>
						<div className={styles.field}>
							<div className={styles.fieldLabel}>Buyer PO ID</div>
							<input
								className={styles.input}
								type="text"
								placeholder="Buyer PO ID"
								value={pageState.buyerPoId}
								onChange={(e) => setPageState((prev) => ({ ...prev, buyerPoId: e.target.value }))}
							/>
						</div>
					</div>

					<div className={styles.twoCol}>
						<div className={styles.field}>
							<div className={styles.fieldLabel}>
								Sale Price <span className={styles.required}>*</span>
							</div>
							<input
								className={styles.input}
								type="number"
								inputMode="decimal"
								min={0}
								step="1"
								placeholder="0.00"
								value={pageState.salePrice}
								onChange={(e) => handleSalePriceChange(e.target.value)}
							/>
						</div>
						<div className={styles.field}>
							<div className={styles.fieldLabel}>Price per Ticket</div>
							<input
								className={styles.input}
								type="number"
								inputMode="decimal"
								min={0}
								step="1"
								placeholder="0.00"
								value={pageState.pricePerTicket}
								onChange={(e) => handlePerTicketChange(e.target.value)}
							/>
						</div>
					</div>

					<div className={styles.field}>
						<div className={styles.fieldLabel}>Quantity</div>
						<input className={styles.input} type="number" value={quantity} disabled readOnly />
					</div>

					<div className={styles.field}>
						<div className={styles.fieldLabel}>Shipping Notes</div>
						<textarea
							className={styles.textarea}
							placeholder="Add shipping notes..."
							value={pageState.shippingNotes}
							onChange={(e) => setPageState((prev) => ({ ...prev, shippingNotes: e.target.value }))}
						/>
					</div>

					<div className={styles.field}>
						<div className={styles.fieldLabel}>Seats</div>
						{availableSeats.length ? (
							<div className={styles.seatList}>
								<label className={styles.seatRow}>
									<Checkbox controlSize="sm" isChecked={allSeatsSelected} onChange={toggleSelectAll} />
									<span>Select All</span>
								</label>
								{availableSeats.map((seat) => (
									<label key={seat.value} className={styles.seatRow}>
										<Checkbox
											controlSize="sm"
											isChecked={pageState.selectedSeats.includes(seat.value)}
											onChange={() => toggleSeat(seat.value)}
										/>
										<span>{seat.label}</span>
									</label>
								))}
							</div>
						) : (
							<div className={styles.emptySeats}>No seats available.</div>
						)}
					</div>
				</div>

				<div className={styles.refCol}>
					<div className={styles.colTitle}>Listing reference</div>
					<div className={styles.refBlock}>
						<div className={styles.refLabel}>Current price</div>
						<div className={styles.refValue}>{formatCurrency(item?.Item_Price ?? 0)}</div>
					</div>
					<div className={styles.refBlock}>
						<div className={styles.refLabel}>Cost</div>
						<div className={styles.refValue}>{formatCurrency(item?.Item_Cost ?? 0)}</div>
					</div>
					<div className={styles.refBlock}>
						<div className={styles.refLabel}>Internal notes</div>
						<div className={styles.refNotes}>{item?.Internal_Notes?.trim() || "No internal notes"}</div>
					</div>
					<div className={styles.refBlock}>
						<div className={styles.refLabel}>External notes</div>
						<div className={styles.refNotes}>{item?.External_Notes?.trim() || "No external notes"}</div>
					</div>
				</div>
			</div>

			<div className={styles.footer}>
				<label className={styles.complete}>
					<Checkbox
						controlSize="sm"
						isChecked={pageState.markComplete}
						onChange={() => setPageState((prev) => ({ ...prev, markComplete: !prev.markComplete }))}
					/>
					<span>Mark Complete</span>
				</label>
				<div className={styles.buttons}>
					<Button onClick={closeAndReset} variant={ButtonVariant.Secondary} disabled={loading || printing}>
						Cancel
					</Button>
					<Button
						onClick={() => handleSubmit(true)}
						variant={ButtonVariant.Secondary}
						isLoading={loading || printing}
						disabled={!canSubmit || printing}>
						Finish & Print
					</Button>
					<Button
						onClick={() => handleSubmit(false)}
						variant={ButtonVariant.Primary}
						isLoading={loading}
						disabled={!canSubmit || printing}>
						Finish
					</Button>
				</div>
			</div>
		</div>
	);
};

export default InternalInvoiceForm;
